import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import PageHeader from "@/components/PageHeader";

/**
 * V8.1 · Orixa Mission Control — super_admin cockpit.
 * Read-only aggregations. NEVER displays secret values.
 */
export default function MissionControl() {
  const [dash, setDash] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [q, setQ] = useState("");
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [d, t] = await Promise.all([
          api.get("/mission-control/dashboard"),
          api.get("/mission-control/tenants?limit=50"),
        ]);
        setDash(d.data);
        setTenants(t.data.tenants || []);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Access denied");
      }
    })();
  }, []);

  const search = async () => {
    const { data } = await api.get(`/mission-control/tenants?q=${encodeURIComponent(q)}&limit=50`);
    setTenants(data.tenants || []);
  };

  if (err) {
    return (
      <div data-testid="mc-forbidden" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-4">
        Mission Control is restricted to super_admin accounts.
      </div>
    );
  }

  return (
    <div data-testid="mission-control-page" className="space-y-6">
      <PageHeader
        title="Orixa Mission Control"
        subtitle="Cockpit global du super administrateur · lecture seule · aucun secret exposé."
      />

      {dash && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat testid="mc-users" label="Users" value={dash.identity.users} />
            <Stat testid="mc-orgs" label="Organizations" value={dash.identity.organizations} />
            <Stat testid="mc-asm-total" label="Assessments (total)" value={dash.totals.assessments} />
            <Stat testid="mc-active-subs" label="Active subscriptions" value={dash.totals.active_subscriptions} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card title="Activity · 24h" testid="mc-activity-24h">
              <li>Assessments: <b>{dash.activity_24h.assessments}</b></li>
              <li>Emails: <b>{dash.activity_24h.emails}</b></li>
              <li>Audit events: <b>{dash.activity_24h.audit_events}</b></li>
              <li>Paddle events: <b>{dash.activity_24h.paddle_events}</b></li>
            </Card>
            <Card title="Knowledge Graph" testid="mc-kg">
              <li>Sources: <b>{dash.knowledge_graph.sources}</b></li>
              <li>Claims: <b>{dash.knowledge_graph.claims}</b></li>
              <li>Evidence: <b>{dash.knowledge_graph.evidence}</b></li>
              <li>Impact edges: <b>{dash.knowledge_graph.impact_edges}</b></li>
            </Card>
            <Card title="Product catalog" testid="mc-catalog">
              <li>Products: <b>{dash.totals.products}</b></li>
              <li>Ledger 30d: <b>{dash.totals.ledger_entries_30d}</b></li>
              <li>Investment docs: <b>{dash.totals.investment_docs}</b></li>
              <li>Leads: <b>{dash.totals.leads}</b></li>
            </Card>
          </div>

          <div data-testid="mc-secrets-health" className="bg-card border border-border rounded-2xl p-4 sm:p-6">
            <div className="text-overline text-muted-foreground mb-3">Secrets health · presence only</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {Object.entries(dash.secrets_health).map(([k, v]) => (
                <div key={k} data-testid={`mc-secret-${k}`} className="flex items-center justify-between border border-slate-100 rounded px-3 py-1.5 text-xs">
                  <span className="text-slate-700 truncate mr-2">{k}</span>
                  <span className={`font-mono ${v === "SET" ? "text-emerald-700" : "text-amber-700"}`}>{v}</span>
                </div>
              ))}
            </div>
            <div className="text-[11px] text-slate-400 mt-2">Never displays actual secret values.</div>
          </div>
        </>
      )}

      <div className="bg-card border border-border rounded-2xl p-4 sm:p-6">
        <div className="text-overline text-muted-foreground mb-3">Tenant explorer</div>
        <div className="flex gap-2 mb-4">
          <input
            data-testid="mc-tenant-search"
            value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && search()}
            placeholder="Search by name, slug, or id"
            className="flex-1 border border-border rounded-lg px-3 py-2 text-sm"
          />
          <button data-testid="mc-tenant-search-btn" onClick={search} className="px-3 py-2 text-sm rounded-lg bg-primary text-primary-foreground">Search</button>
        </div>
        {tenants.length === 0 && <div className="text-sm text-muted-foreground">No tenant matches.</div>}
        {tenants.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-slate-500">
                <tr>
                  <th className="text-left py-2 pr-4">Name</th>
                  <th className="text-left py-2 pr-4">Plan</th>
                  <th className="text-right py-2 pr-4">Users</th>
                  <th className="text-right py-2 pr-4">Assessments</th>
                  <th className="text-left py-2 pr-4">Status</th>
                </tr>
              </thead>
              <tbody>
                {tenants.map((t) => (
                  <tr key={t.id} data-testid={`mc-tenant-row-${t.id}`} className="border-t border-border">
                    <td className="py-2 pr-4">
                      <Link to={`/mission-control/tenants/${t.id}`} className="text-primary underline">{t.name || t.id}</Link>
                      <div className="text-[11px] text-slate-500">{t.id}</div>
                    </td>
                    <td className="py-2 pr-4">{t.plan || "—"}</td>
                    <td className="py-2 pr-4 text-right">{t.users}</td>
                    <td className="py-2 pr-4 text-right">{t.assessments}</td>
                    <td className="py-2 pr-4"><span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">{t.subscription_status || "—"}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ testid, label, value }) {
  return (
    <div data-testid={testid} className="bg-card border border-border rounded-2xl p-4">
      <div className="text-overline text-muted-foreground">{label}</div>
      <div className="text-3xl font-serif mt-1">{value ?? 0}</div>
    </div>
  );
}

function Card({ title, testid, children }) {
  return (
    <div data-testid={testid} className="bg-card border border-border rounded-2xl p-4 sm:p-6">
      <div className="text-overline text-muted-foreground mb-3">{title}</div>
      <ul className="text-sm space-y-1.5 text-slate-700">{children}</ul>
    </div>
  );
}
